import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import logger from 'utils/log';
import { CognitoRefreshResponseSchema } from './schemas';
import { EXHIBITION_STAGE, ExhibitionStandaloneUriMapping } from 'utils/ui/stage';
import { getStorageItem, removeStorageItem, setStorageItem } from 'utils/ui/storage';

/**
 * This file is used only for the standalone version of 3D Exhibition.
 */

export type TAuthToken = {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
};

// Disable because we explicitly want to check the type of an input that could be anything
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function verifyAuthToken(input: any): input is TAuthToken {
  return (
    input &&
    typeof input?.idToken === 'string' &&
    typeof input?.accessToken === 'string' &&
    typeof input?.refreshToken === 'string' &&
    typeof input?.expiresAt === 'number'
  );
}

export type TSilentRefreshResult =
  { status: 'refreshed' } | { status: 'retry' } | { status: 'rejected'; refreshToken: string };

const RETRY: TSilentRefreshResult = { status: 'retry' };

/** Token-endpoint statuses that are final for the refresh token sent (invalid_grant, invalid_client). */
const PERMANENT_REFRESH_STATUSES: ReadonlySet<number> = new Set([400, 401]);

const clientId = process.env.AUTH_CLIENTID ?? '';
const authDomain = process.env.AUTH_DOMAIN ?? '';

if (!clientId || !authDomain) {
  console.error(`Authentication environment variable(s) missing: 
    ${clientId ? 'AUTH_CLIENTID' : ''} 
    ${authDomain ? 'AUTH_DOMAINID' : ''}`);
}

const origin = getAuthenticationOrigin();

const redirectUri = `${origin}/auth`;

/**
 * Returns a readily configured OAuthClient.
 * @returns {UserManager} configured oidc-client-ts UserManager
 */

export function getOAuthClient(): UserManager {
  return new UserManager({
    authority: authDomain,
    client_id: clientId,
    redirect_uri: redirectUri,
    // register the state store so we can set and retrieve the authentication state
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    // no revoke of "access token" (https://github.com/authts/oidc-client-ts/issues/262)
    revokeTokenTypes: ['refresh_token'],
    // no silent renew via "prompt=none" (https://github.com/authts/oidc-client-ts/issues/366)
    automaticSilentRenew: false,
    metadata: {
      authorization_endpoint: `${authDomain}/oauth2/authorize`,
      response_types_supported: ['code', 'token'],
      scopes_supported: ['openid', 'email', 'phone', 'profile'],
      subject_types_supported: ['public'],
      token_endpoint: `${authDomain}/oauth2/token`,
      token_endpoint_auth_methods_supported: ['client_secret_basic', 'client_secret_post'],
      userinfo_endpoint: `${authDomain}/oauth2/userInfo`,
      end_session_endpoint: `${authDomain}/logout?client_id=${clientId}&logout_uri=${redirectUri}`,
    },
  });
}

/**
 * Returns true when token exists and is not expired. Does not try to refesh an expired token!
 */
export function isAuthenticated(): boolean {
  const token = getStorageItem('sessionStorage', 'auth:token');
  if (!verifyAuthToken(token)) return false;
  const expiresAt = new Date(token.expiresAt * 1000 - 5000); // 5 sec grace time for leniency
  const currentDate = new Date();
  const authenticated = expiresAt > currentDate;
  return authenticated;
}

/**
 * Sets the required token in the session storage under the key auth:token
 * @param user The oidc-client-ts instance of the user
 */

export function setAuthInSessionStorage(user: User) {
  const { id_token, access_token, refresh_token, expires_at } = user;
  if (!id_token || !access_token || !refresh_token || !expires_at) {
    throw new Error('Provided user is not logged in. Cannot set tokens.');
  }

  const token: TAuthToken = {
    idToken: id_token,
    accessToken: access_token,
    refreshToken: refresh_token,
    expiresAt: expires_at,
  };
  setStorageItem('sessionStorage', 'auth:token', token);

  // Mark this user as a dealer long-term. This will help us make assumptions about the user in other parts of the app.
  // For example, it helps us re-route universal links to dealer-specific views.
  setStorageItem('localStorage', 'dealer', 'true');
}

/**
 * Refreshes the access token and the id token for the provided user, if possible.
 *
 * @param userClient the user client to use for the refresh
 * @param user the user to use for the refresh
 * @returns {boolean} If successful, will return true, otherwise false.
 */
export async function refreshTokens(userClient: UserManager, user: User): Promise<boolean> {
  if (!user.refresh_token || !userClient) {
    logger.warn("oAuth token expired and can't refresh it");
    return false;
  }
  let result: User | null;
  try {
    result = await userClient.signinSilent();
  } catch (error) {
    logger.error('Error during silent sign-in', { error });
    return false;
  }
  if (result === null) {
    logger.warn('Error refreshing oAuth tokens');
    return false;
  }
  user.access_token = result.access_token;
  user.id_token = result.id_token;
  user.expires_at = result.expires_at;
  setAuthInSessionStorage(user);
  logger.debug('oAuth tokens refreshed', { result });
  return true;
}

/**
 * Silently refreshes the stored auth token using the OAuth2 `refresh_token` grant.
 *
 * Unlike {@link refreshTokens}, this does not need an oidc-client-ts `User` object, so it also
 * works on PR preview domains where only `auth:token` is present (no persisted oidc `User`).
 * The refresh-token grant is a direct POST to the token endpoint keyed on `client_id` and does
 * not depend on redirect-uri whitelisting. (Portal-embedded sessions without a refresh token
 * cannot self-refresh and short-circuit to `retry`.)
 *
 * @returns `refreshed` when a fresh token was persisted; `rejected` (with the refresh token that was
 * sent) when the token endpoint answered 400 or 401, which is final for that refresh token; `retry`
 * for anything that may pass later (network error, 5xx, 429, a bad body, nothing to refresh yet).
 */
export async function silentRefresh(): Promise<TSilentRefreshResult> {
  const token = getStorageItem('sessionStorage', 'auth:token');
  if (!verifyAuthToken(token) || !token.refreshToken) return RETRY;
  if (!authDomain || !clientId) {
    logger.warn('[silentRefresh] Missing auth configuration; cannot refresh');
    return RETRY;
  }

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: clientId,
    refresh_token: token.refreshToken,
  });

  let response: Response;
  try {
    response = await fetch(`${authDomain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
  } catch (error) {
    logger.warn('[silentRefresh] Token endpoint request failed', { error });
    return RETRY;
  }

  if (!response.ok) {
    logger.warn('[silentRefresh] Token endpoint returned a non-OK response', { status: response.status });
    return PERMANENT_REFRESH_STATUSES.has(response.status)
      ? { status: 'rejected', refreshToken: token.refreshToken }
      : RETRY;
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch (error) {
    logger.error('[silentRefresh] Failed to parse refresh response', { error });
    return RETRY;
  }

  const parsed = CognitoRefreshResponseSchema.safeParse(json);
  if (!parsed.success) {
    logger.error('[silentRefresh] Malformed refresh response', { error: parsed.error });
    return RETRY;
  }

  const refreshed: TAuthToken = {
    idToken: parsed.data.id_token ?? token.idToken,
    accessToken: parsed.data.access_token,
    refreshToken: parsed.data.refresh_token ?? token.refreshToken,
    expiresAt: Math.floor(Date.now() / 1000) + parsed.data.expires_in,
  };
  setStorageItem('sessionStorage', 'auth:token', refreshed);
  logger.debug('[silentRefresh] Tokens refreshed');
  return { status: 'refreshed' };
}

/**
 * Removes all credentials and login information and logs out at the IdP.
 * The user will be redirected back to the login page.
 *
 * Uses window.location.assign directly for reliable cross-browser navigation.
 * oidc-client-ts signoutRedirect relies on the 'pageshow' event which can hang
 * silently in Firefox, preventing the redirect from ever happening.
 *
 * Also clears auth:url so that after re-login the user lands on the homepage
 * rather than being sent back to a previous session URL.
 */
export async function logout(): Promise<void> {
  const userClient = getOAuthClient();

  const logoutEndpoint = userClient.settings.metadata?.end_session_endpoint;
  if (!logoutEndpoint) {
    throw new Error('Missing end_session_endpoint in OAuth client metadata');
  }

  await userClient.removeUser();
  await userClient.clearStaleState();
  removeStorageItem('sessionStorage', 'auth:token');
  removeStorageItem('sessionStorage', 'auth:url');
  window.location.assign(logoutEndpoint);
}

/**
 * Clears all authentication-related data from storage
 * This includes dealer flags and all auth tokens stored in localStorage and sessionStorage
 * Used when loading customer views to ensure they don't inherit dealer permissions
 *
 * @throws {Error} If storage operations fail (e.g., in SSR context or storage unavailable)
 */
export function clearAuthenticationStorage(): void {
  try {
    if (typeof window === 'undefined') {
      throw new Error('clearAuthenticationStorage can only be called in browser context');
    }

    // Clear dealer flag from localStorage
    removeStorageItem('localStorage', 'dealer');

    // Clear all auth-related data from sessionStorage
    removeStorageItem('sessionStorage', 'auth:token');
    removeStorageItem('sessionStorage', 'auth:dealerId');
    removeStorageItem('sessionStorage', 'auth:brand');
    removeStorageItem('sessionStorage', 'auth:market');
  } catch (error) {
    logger.error('[clearAuthenticationStorage] Failed to clear authentication storage', error);
    throw error;
  }
}

export function getAuthenticationOrigin() {
  // if no window, we are running this in node
  if (typeof window === 'undefined') {
    return '';
  }

  // if the origin is one of the expected stage URLs or localhost then use it
  if (
    window.location.origin === ExhibitionStandaloneUriMapping[EXHIBITION_STAGE] ||
    window.location.hostname === 'localhost'
  ) {
    return window.location.origin;
  }

  // if we seem to be running 3DE on an unknown domain (temp PR domain) then we use the dev environment for authentication
  return ExhibitionStandaloneUriMapping['dev'];
}

export function isTrustedAppOrigin(origin: string | null): boolean {
  if (!origin) return false;
  const validOrigins = ['3dexhibition.avp.tech', 'localhost:3000'];
  return validOrigins.some((validOrigin) => origin.endsWith(validOrigin));
}
